import { Html } from '@react-three/drei';
import { ThreeEvent } from '@react-three/fiber';
import { fromArrayBuffer } from 'geotiff';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import * as THREE from 'three';
import { useAtomValue } from 'jotai';
import { createResourceAtom } from 'lib/atoms';
import { Grid, StructureGridCollection } from 'types/api-responses';
import {
  findGridMetadata,
  extractAffineTransformData,
  convertAffineToFeet,
} from './utils';
import styles from './well-3d-viewer.module.scss';

interface Surface {
  structureGridId: number;
  surfaceGTiff: string;
}

interface SurfaceResponse {
  surfaces: Surface[];
}

interface StructureGridSurfacesProps {
  collections: StructureGridCollection[];
  individualGrids?: Grid[];
  selectedGridIds: number[];
  paddedBoundingBoxGeometry: GeoJSON.Polygon | null;
}

const HoverableSurfaceMesh = ({ mesh }: { mesh: THREE.Mesh }) => {
  const [hovered, setHovered] = useState(false);
  const [hoverPosition, setHoverPosition] = useState<THREE.Vector3 | null>(
    null
  );
  const groupRef = useRef<THREE.Group | null>(null);

  // Convert world-space event.point to the group's local space so the
  // tooltip position correctly accounts for all parent transforms (VE scaling).
  const toLocalPosition = (worldPoint: THREE.Vector3): THREE.Vector3 => {
    if (!groupRef.current) return worldPoint.clone();
    return groupRef.current.worldToLocal(worldPoint.clone());
  };

  const handlePointerOver = (event: ThreeEvent<PointerEvent>) => {
    event.stopPropagation();
    setHovered(true);
    if (event.point) setHoverPosition(toLocalPosition(event.point));
  };

  const handlePointerOut = (event: ThreeEvent<PointerEvent>) => {
    event.stopPropagation();
    setHovered(false);
    setHoverPosition(null);
  };

  const handlePointerMove = (event: ThreeEvent<PointerEvent>) => {
    if (hovered && event.point) {
      setHoverPosition(toLocalPosition(event.point));
    }
  };

  const { displayName, source, geologicInterval } = mesh.userData;

  return (
    <group
      ref={(node) => {
        groupRef.current = node;
      }}
    >
      <mesh
        geometry={mesh.geometry}
        material={mesh.material}
        position={mesh.position}
        rotation={mesh.rotation}
        scale={mesh.scale}
        onPointerOver={handlePointerOver}
        onPointerOut={handlePointerOut}
        onPointerMove={handlePointerMove}
      />
      {hovered && hoverPosition && (
        <Html position={hoverPosition} className={styles.wellTooltip}>
          <div className={styles.tooltipContent}>
            <strong>{geologicInterval}</strong>
            <br />
            <small>
              {displayName} - Source: {source}
            </small>
          </div>
        </Html>
      )}
    </group>
  );
};

const createSurfaceMesh = (
  surface: Surface,
  elevations: Float32Array,
  tiffImage: any,
  collections: StructureGridCollection[],
  individualGrids: Grid[]
): THREE.Mesh | null => {
  try {
    const { displayName, source, geologicInterval, displayColor } =
      findGridMetadata(
        collections,
        surface.structureGridId,
        individualGrids || []
      );

    // Extract affine transformation data using utility function
    const affineData = extractAffineTransformData(tiffImage);
    const { originX, originY, scaleX, scaleY, noDataValue, width, height } =
      affineData;

    // Single array for all vertex positions
    const positions: number[] = [];
    // Map each valid raster pixel to its vertex index in `positions`. Replaces a
    // `validIndices` array scanned with includes()/indexOf() in the triangulation
    // loop below — the stored value equals the old `validIndices.indexOf(pixel)`,
    // so the index buffer is identical, but lookups drop from O(V) to O(1)
    // (overall O(width·height) instead of O(width·height·V)).
    const pixelToVertex = new Map<number, number>();
    let validVertexCount = 0;

    // Extract valid vertices and track their indices
    for (let row = 0; row < height; row++) {
      for (let col = 0; col < width; col++) {
        const pixelIndex = row * width + col;
        const z = elevations[pixelIndex];

        if (z !== noDataValue && !isNaN(z)) {
          // Convert affine coordinates to feet using utility function
          const { xFeet, yFeet } = convertAffineToFeet(
            originX,
            originY,
            scaleX,
            scaleY,
            col,
            row
          );

          positions.push(xFeet, yFeet, z);
          pixelToVertex.set(pixelIndex, validVertexCount);
          validVertexCount++;
        }
      }
    }

    if (validVertexCount === 0) return null;

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute(
      'position',
      new THREE.BufferAttribute(new Float32Array(positions), 3)
    );

    // Build indices for triangulation using valid vertex indices
    const indices: number[] = [];
    for (let row = 0; row < height - 1; row++) {
      for (let col = 0; col < width - 1; col++) {
        const currentIndex = row * width + col;
        const nextColIndex = currentIndex + 1;
        const nextRowIndex = (row + 1) * width + col;
        const nextRowNextColIndex = nextRowIndex + 1;

        // O(1) lookups: a vertex index (or undefined when that pixel had no
        // valid elevation). Emitting a quad only when all four corners exist.
        const v0 = pixelToVertex.get(currentIndex);
        const v1 = pixelToVertex.get(nextRowIndex);
        const v2 = pixelToVertex.get(nextColIndex);
        const v3 = pixelToVertex.get(nextRowNextColIndex);

        if (
          v0 !== undefined &&
          v1 !== undefined &&
          v2 !== undefined &&
          v3 !== undefined
        ) {
          indices.push(v0, v1, v2);
          indices.push(v2, v1, v3);
        }
      }
    }

    if (indices.length === 0) return null;

    geometry.setIndex(indices);
    geometry.computeVertexNormals();
    geometry.computeBoundingBox();
    const material = new THREE.MeshPhongMaterial({
      color: displayColor,
      transparent: true,
      opacity: 0.35,
      side: THREE.DoubleSide,
      depthWrite: true,
      depthTest: false,
    });

    const mesh = new THREE.Mesh(geometry, material);
    mesh.visible = true;
    mesh.frustumCulled = false;
    mesh.position.set(0, 0, 0);

    mesh.userData = {
      structureGridId: surface.structureGridId,
      displayName,
      source,
      geologicInterval,
      coordinateSystem: 'affine_transformation_coordinates',
      pureGeoTIFF: true,
      affineTransform: {
        origin: [originX, originY],
        resolution: [scaleX, scaleY],
      },
      vertexCount: positions.length / 3,
      triangleCount: indices.length / 3,
      displayColor,
    };

    return mesh;
  } catch (error) {
    console.error(
      'Error creating mesh for surface',
      surface.structureGridId,
      ':',
      error
    );
    return null;
  }
};

export const StructureGridSurfaces: React.FC<StructureGridSurfacesProps> = ({
  collections,
  individualGrids,
  selectedGridIds,
  paddedBoundingBoxGeometry,
}) => {
  const [meshes, setMeshes] = useState<THREE.Mesh[]>([]);
  const lastSelectedIdsRef = useRef<string>('');

  const selectedGridIdsKey = useMemo(
    () => [...selectedGridIds].sort((a, b) => a - b).join(','),
    [selectedGridIds]
  );

  const surfaceUrl = useMemo(() => {
    if (!selectedGridIds.length || !paddedBoundingBoxGeometry) return undefined;

    const selectedGrid =
      collections
        .flatMap((collection) => collection.grids)
        .find((grid) => selectedGridIds.includes(grid.gridId)) ||
      individualGrids?.find((grid) => selectedGridIds.includes(grid.gridId));

    if (!selectedGrid?.href) return undefined;

    const params = new URLSearchParams({
      search_geometry: JSON.stringify(paddedBoundingBoxGeometry),
      structure_grid_ids: selectedGridIds.join(','),
    });

    return `${selectedGrid.href}?${params.toString()}`;
  }, [
    collections,
    individualGrids,
    selectedGridIds,
    paddedBoundingBoxGeometry,
  ]);

  const surfaceResourceAtom = useMemo(
    () => createResourceAtom<SurfaceResponse>(surfaceUrl),
    [surfaceUrl]
  );

  const surfaceData = useAtomValue(surfaceResourceAtom);

  const processSurfaces = useCallback(
    async (surfaces: Surface[]): Promise<THREE.Mesh[]> => {
      try {
        if (!surfaces || !Array.isArray(surfaces)) return [];

        const newMeshes: THREE.Mesh[] = [];

        for (const surface of surfaces) {
          if (!selectedGridIds.includes(surface.structureGridId)) continue;

          try {
            const binaryString = atob(surface.surfaceGTiff);
            const bytes = new Uint8Array(binaryString.length);
            for (let i = 0; i < binaryString.length; i++) {
              bytes[i] = binaryString.charCodeAt(i);
            }

            const tiff = await fromArrayBuffer(bytes.buffer);
            const image = await tiff.getImage();
            const rasters = await image.readRasters();
            const elevationData = rasters[0] as Float32Array;

            const mesh = createSurfaceMesh(
              surface,
              elevationData,
              image,
              collections,
              individualGrids || []
            );

            if (mesh) {
              newMeshes.push(mesh);
            }
          } catch (error) {
            console.warn(
              `Error processing surface ${surface.structureGridId}:`,
              error
            );
          }
        }

        return newMeshes;
      } catch (error) {
        console.error('Error in processSurfaces:', error);
        return [];
      }
    },
    [selectedGridIds, collections, individualGrids]
  );

  useEffect(() => {
    if (surfaceData && surfaceData.data && !('error' in surfaceData.data)) {
      const surfaces = surfaceData.data.surfaces;
      if (surfaces && surfaces.length > 0) {
        processSurfaces(surfaces)
          .then(setMeshes)
          .catch(() => setMeshes([]));
      }
    } else if (surfaceData && surfaceData.data && 'error' in surfaceData.data) {
      setMeshes([]);
    } else if (!surfaceData.loading && !surfaceData.data) {
      setMeshes([]);
    }
  }, [surfaceData, processSurfaces]);

  useEffect(() => {
    if (selectedGridIdsKey !== lastSelectedIdsRef.current) {
      lastSelectedIdsRef.current = selectedGridIdsKey;
      setMeshes([]);
    }
  }, [selectedGridIdsKey]);

  if (!selectedGridIds.length || meshes.length === 0) {
    return null;
  }

  return (
    <group>
      {meshes.map((mesh, index) => (
        <HoverableSurfaceMesh key={`surface-mesh-${index}`} mesh={mesh} />
      ))}
    </group>
  );
};
